<script lang="ts" setup>
import { getNode } from '@formkit/core';
const localePath = useLocalePath()

definePageMeta({
  layout: 'auth'
})

const {t} = useI18n({
  useScope: 'global'
})

const loginForm = reactive({
  username: '',
  password: ''
})

const otpState = reactive({
  active: false,
  sessionId: '',
  code: '',
  resendCooldown: 0,
  method: '' as '' | 'totp' | 'email' | 'sms',
})

const channelState = reactive({
  active: false,
  preSessionId: '',
  channels: [] as { type: string; maskedValue: string }[],
})

let resendTimer: ReturnType<typeof setInterval> | null = null

function startResendCooldown() {
  otpState.resendCooldown = 60
  resendTimer = setInterval(() => {
    otpState.resendCooldown--
    if (otpState.resendCooldown <= 0 && resendTimer) {
      clearInterval(resendTimer)
      resendTimer = null
    }
  }, 1000)
}

onUnmounted(() => {
  if (resendTimer) clearInterval(resendTimer)
})

const submitHandler = async (formData, node) => {
  try {
    const {login} = useAuthUser()

    const result = await login({
      username: formData.username,
      password: formData.password
    })

    if (result.requiresOtpChannelSelection) {
      channelState.active = true
      channelState.preSessionId = result.preSessionId
      channelState.channels = result.channels
      return
    }

    if (result.requiresOtp) {
      otpState.active = true
      otpState.sessionId = result.otpSessionId
      otpState.method = (result.otpChannelType as typeof otpState.method) || ''
      otpState.code = ''
      startResendCooldown()
      return
    }

    navigateAfterLogin()
  } catch (e) {
    node.setErrors(
      [e.message],
    )
    return false;
  }
}

const otpSubmitHandler = async (formData, node) => {
  try {
    const {verifyOtp} = useAuthUser()

    await verifyOtp({
      otpSessionId: otpState.sessionId,
      code: formData.code,
    })

    navigateAfterLogin()
  } catch (e) {
    node.setErrors(
      [e.message],
    )
    return false;
  }
}

const resendOtp = async () => {
  if (otpState.resendCooldown > 0) return
  try {
    const client = useNuxtApp().$client
    const res = await client.auth.resendOtp.mutate({
      otpSessionId: otpState.sessionId,
    })
    otpState.sessionId = res.otpSessionId
    startResendCooldown()
  } catch (e) {
    console.error('Failed to resend OTP:', e.message)
  }
}

const selectChannelHandler = async (index: number) => {
  try {
    const client = useNuxtApp().$client
    const res = await client.auth.selectOtpChannel.mutate({
      preSessionId: channelState.preSessionId,
      channelIndex: index,
    })
    channelState.active = false
    otpState.active = true
    otpState.sessionId = res.otpSessionId
    otpState.method = (res.otpChannelType as typeof otpState.method) || ''
    otpState.code = ''
    startResendCooldown()
  } catch (e) {
    console.error('Failed to select OTP channel:', (e as Error).message)
  }
}

const backToLogin = () => {
  channelState.active = false
  channelState.preSessionId = ''
  channelState.channels = []
  otpState.active = false
  otpState.sessionId = ''
  otpState.code = ''
  otpState.resendCooldown = 0
  otpState.method = ''
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
}

const onOtpInput = (value: string) => {
  if (value?.length === 6) {
    getNode('otp-form')?.submit()
  }
}

watch(() => otpState.active, (active) => {
  if (active) {
    nextTick(() => {
      const input = document.querySelector<HTMLInputElement>('#otp-form .otp__input')
      input?.focus()
    })
  }
})

function navigateAfterLogin() {
  const {query} = useRoute()
  if (query.redirect) {
    navigateTo(localePath(`${query.redirect}`));
    return;
  }
  navigateTo(localePath('/dealer/cars'))
}

</script>

<template>
  <div>
    <!-- Login Form -->
    <FormKit
      v-if="!otpState.active && !channelState.active"
      id="login-form"
      v-model="loginForm"
      type="form"
      :actions="false"
      @submit="submitHandler"
    >
      <h2 class="form-title">
        {{ t('auth.login.title') }}
      </h2>

      <FormKit
        type="text"
        name="username"
        :placeholder="t('common.labels.username')"
        validation="required"
      />

      <FormKit
        type="password"
        name="password"
        :placeholder="t('common.labels.password')"
      />

      <div class="form-nav">
        <div class="link">
          <NuxtLink :to="localePath('/auth/register')">
            {{ t('auth.login.register') }}
          </NuxtLink>
        </div>
        <div class="link">
          <NuxtLink :to="localePath('/auth/reset')">
            {{ t('auth.login.forgotPassword') }}
          </NuxtLink>
        </div>
      </div>

      <FormKit
        type="submit"
        variant="primary"
        width="full"
        :label="t('auth.login.submit')"
      />
    </FormKit>

    <!-- Channel Selection -->
    <div
      v-else-if="channelState.active"
      class="channel-selection"
    >
      <h2 class="form-title">
        {{ t('auth.otp.selectChannel') }}
      </h2>
      <p class="otp-description">
        {{ t('auth.otp.selectChannelDescription') }}
      </p>
      <div class="channel-list">
        <button
          v-for="(channel, index) in channelState.channels"
          :key="index"
          type="button"
          class="channel-btn"
          @click="selectChannelHandler(index)"
        >
          <span class="channel-type">{{ channel.type === 'email' ? t('auth.otp.channelEmail') : t('auth.otp.channelSms') }}</span>
          <span class="channel-value">{{ channel.maskedValue }}</span>
        </button>
      </div>
      <button
        type="button"
        class="back-btn"
        @click="backToLogin"
      >
        {{ t('auth.otp.backToLogin') }}
      </button>
    </div>

    <!-- OTP Form -->
    <FormKit
      v-else
      id="otp-form"
      type="form"
      :actions="false"
      @submit="otpSubmitHandler"
    >
      <h2 class="form-title">
        {{ t('auth.otp.title') }}
      </h2>

      <p class="otp-description">
        <template v-if="otpState.method === 'totp'">{{ t('auth.otp.descriptionTotp') }}</template>
        <template v-else-if="otpState.method === 'email'">{{ t('auth.otp.descriptionEmail') }}</template>
        <template v-else-if="otpState.method === 'sms'">{{ t('auth.otp.descriptionSms') }}</template>
        <template v-else>{{ t('auth.otp.description') }}</template>
      </p>

      <div class="otp-input-wrapper">
        <FormKit
          type="otp"
          name="code"
          :digits="6"
          validation="required|length:6"
          @input="onOtpInput"
        />
      </div>

      <div class="otp-actions">
        <button
          v-if="otpState.method !== 'totp'"
          type="button"
          class="resend-btn"
          :disabled="otpState.resendCooldown > 0"
          @click="resendOtp"
        >
          <template v-if="otpState.resendCooldown > 0">
            {{ t('auth.otp.resendIn', { seconds: otpState.resendCooldown }) }}
          </template>
          <template v-else>
            {{ t('auth.otp.resend') }}
          </template>
        </button>

        <button
          type="button"
          class="back-btn"
          @click="backToLogin"
        >
          {{ t('auth.otp.backToLogin') }}
        </button>
      </div>
    </FormKit>

    <NuxtLink
      v-if="!otpState.active && !channelState.active"
      :to="localePath('/guest/cars')"
    >
      <FormKit
        type="button"
        variant="outline"
        width="full"
        :label="t('auth.login.asGuest')"
      />
    </NuxtLink>
  </div>
</template>

<style scoped lang="scss">

  .form-title {
    font-weight: 700;
    font-size: 18px;
    text-align: center;
    margin-bottom: 30px;
  }

  .form-item {
    width: 100%;
    margin-bottom: 25px;
  }

  .form-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 5px 0 30px;

    .link {
      font-size: 12px;
    }
  }

  .btn {
    width: 100%;
  }

  .otp-description {
    font-size: 14px;
    color: #6E6E8A;
    text-align: center;
    margin-bottom: 24px;
    line-height: 1.5;
  }

  .otp-input-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
  }

  .otp-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .resend-btn {
    background: none;
    border: none;
    color: #2D2D86;
    font-size: 13px;
    cursor: pointer;
    padding: 4px 8px;

    &:disabled {
      color: #9E9EB8;
      cursor: not-allowed;
    }
  }

  .back-btn {
    background: none;
    border: none;
    color: #6E6E8A;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
    padding: 4px 8px;
  }

  .channel-selection {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .channel-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
  }

  .channel-btn {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    padding: 10px 16px;
    border: 1px solid #D0D0E8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s;

    &:hover {
      border-color: #2D2D86;
    }

    .channel-type {
      font-size: 12px;
      color: #6E6E8A;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .channel-value {
      font-size: 15px;
      font-weight: 600;
      color: #1A1A3E;
      margin-top: 2px;
    }
  }

</style>

<i18n lang="json">
{
  "en": {
    "signIn": "Sign In"
  },
  "ru": {
    "signIn": "Войти"
  }
}
</i18n>
